import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a "price per unit" badge (e.g. "1,70€ pro Tasse").
 *
 * The value is computed server-side in Liquid (variant price ÷ a metafield count,
 * with the variant metafield overriding the product metafield). On variant change
 * the product section is re-rendered, so this component simply swaps in the freshly
 * rendered value from the event's HTML — no math in JS.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} text - The span holding the badge text
 *
 * @extends {Component<Refs>}
 */
class PricePerUnit extends Component {
  requiredRefs = ['text'];

  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], dialog, product-card');
    if (!this.section) return;
    this.section.addEventListener(ThemeEvents.variantUpdate, this.update);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.update);
  }

  /**
   * Swaps in the re-rendered badge for the newly selected variant.
   * @param {CustomEvent} event - The variant update event.
   */
  update = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!html) return;

    const fresh = html.querySelector(`price-per-unit-component[data-block-id="${this.dataset.blockId}"]`);
    if (!fresh) {
      this.style.display = 'none';
      return;
    }

    const freshText = fresh.querySelector('[ref="text"]');
    this.refs.text.innerHTML = freshText ? freshText.innerHTML : '';
    this.style.display = fresh.style.display;
  };
}

if (!customElements.get('price-per-unit-component')) {
  customElements.define('price-per-unit-component', PricePerUnit);
}
